import React from "react"
import PropTypes from "prop-types"
import { format } from "date-fns"
import { SubscriptionStatus } from "@modules/graphql/types"
import { MdInfo } from "react-icons/md"
import AlertHexagon from "@modules/assets/icons/AlertHexagon"
import { subscriptionStatus as text } from "@modules/locales/default.js"
import { getPathToOrgSettings } from "@modules/organization/shared/utils"
import { Notification } from "gatsby-interface"
import styled from "@emotion/styled"
import { useTracker } from "@modules/analytics"
import { FormattedMessage } from "@modules/locales"
import { Text, Spacer } from "gatsby-interface"

const NotificationDetails = styled.div(({ theme, tone }) => ({
  marginTop: theme.space[5],
  paddingTop: theme.space[5],
  borderTop: `1px solid ${theme.tones[tone].light}`,
  fontSize: theme.fontSizes[1],
  color: theme.colors.grey[70],
}))

const propTypes = {
  organizationId: PropTypes.string.isRequired,
  billing: PropTypes.shape({
    creditCard: PropTypes.shape({
      lastFour: PropTypes.string,
      expMonth: PropTypes.number,
      expYear: PropTypes.number,
    }),
    trialDaysRemaining: PropTypes.number,
  }),
  requestedPlanName: PropTypes.string,
  showLink: PropTypes.bool,
  variant: PropTypes.oneOf([`PRIMARY`, `SECONDARY`]),
  className: PropTypes.string,
}

export default function BillingStatusNotification({
  organizationId,
  billing,
  requestedPlanName,
  uiSource,
  showLink = true,
  variant,
  className,
}) {
  const { trackButtonClicked } = useTracker()
  const pathToOrgSettings = getPathToOrgSettings(organizationId)
  const { status, creditCard } = billing
  const props = {
    variant,
    className,
  }

  if (status === SubscriptionStatus.Unpaid) {
    props.tone = `DANGER`
    props.content = text.messages.trialExpired
    props.linkText = text.actions.updatePaymentInformation
    props.linkUrl = `/dashboard/pricing/${organizationId}`
  }

  if (billing && billing.creditCard) {
    if (status === SubscriptionStatus.PastDue) {
      props.tone = `DANGER`
      props.content = (
        <FormattedMessage message={text.messages.paymentIsPastDue} />
      )
      props.linkText = text.actions.updatePaymentInformation
      props.linkUrl = `${pathToOrgSettings}/general#paymentMethod`
    }

    if (status === SubscriptionStatus.Unpaid) {
      props.tone = `DANGER`
      props.content = <BillingMessageCardError lastFour={creditCard.lastFour} />
      props.linkText = text.actions.updatePaymentInformation
      props.linkUrl = pathToOrgSettings
    }

    if (status === SubscriptionStatus.Canceled) {
      props.tone = `DANGER`
      props.content = (
        <FormattedMessage
          message={text.messages.planCanceled}
          values={{
            cancellationDate: format(
              billing.canceledAt ? new Date(billing.canceledAt) : new Date(),
              `MMM do, yyyy`
            ),
          }}
        />
      )
      props.linkText = text.actions.resubscribe
      props.linkUrl = `/dashboard/pricing/${organizationId}`
    }

    const expirationDate = new Date(creditCard.expYear, creditCard.expMonth, 0)

    const hasCreditCardFields =
      billing.creditCard.lastFour &&
      billing.creditCard.expYear &&
      billing.creditCard.expMonth

    if (
      status === SubscriptionStatus.Active &&
      hasCreditCardFields &&
      isCreditCardExpired(expirationDate)
    ) {
      props.tone = `DANGER`
      props.content = (
        <div>
          <FormattedMessage
            message={text.messages.creditCardExpired}
            values={{
              expirationDate: format(expirationDate, `yyyy-MM-dd`),
            }}
          />
          <NotificationDetails tone={`DANGER`}>
            {text.messages.creditCardExpiredUpdate}
          </NotificationDetails>
        </div>
      )
      props.linkText = text.actions.updatePaymentInformation
      props.linkUrl = pathToOrgSettings
    }
  }

  if (requestedPlanName && requestedPlanName === "Enterprise") {
    props.tone = `NEUTRAL`
    props.Icon = MdInfo
    props.content = (
      <div>
        <FormattedMessage
          message={text.messages.planChangeRequested}
          values={{
            requestedPlanName,
          }}
          tags={{
            strong: function(content) {
              return <strong>{content}</strong>
            },
          }}
        />
      </div>
    )
  }

  return props.content ? (
    <Notification
      {...props}
      linkText={showLink ? props.linkText : null}
      onLinkClick={() => {
        trackButtonClicked(props.linkText, {
          organizationId: organizationId,
          uiSource,
        })
      }}
    />
  ) : null
}

BillingStatusNotification.propTypes = propTypes

function BillingMessageCardError({ lastFour }) {
  return (
    <div>
      <FormattedMessage
        message={text.messages.paymentFailed}
        values={{
          lastFour,
        }}
      />
      <NotificationDetails tone={`DANGER`}>
        {text.messages.paymentFailedDescription}
        <Text
          noMarginBottom={true}
          css={theme => ({
            marginTop: theme.space[2],
            fontSize: theme.fontSizes[1],
            fontWeight: theme.fontWeights.bold,
            display: `flex`,
            alignItems: `center`,
          })}
        >
          <AlertHexagon />
          <Spacer size={3} direction="horizontal" />
          {text.messages.paymentFailedUpdate}
        </Text>
      </NotificationDetails>
    </div>
  )
}

BillingMessageCardError.propTypes = {
  lastFour: PropTypes.string.isRequired,
}

function isCreditCardExpired(expirationDate) {
  const nowDate = new Date()

  return nowDate > expirationDate
}
